import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, Plus, X, Search, AlertCircle, CheckCircle2, Clock, Calendar } from "lucide-react";
import { toast } from "sonner";

const DIAS_PT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MESES_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

function dayOffset(year, month) {
  // Portuguese week: Monday=0 ... Sunday=6
  return (new Date(year, month - 1, 1).getDay() + 6) % 7;
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function dayTextColor(isToday, hasOverdue) {
  if (isToday) return "text-[#0b679e]";
  if (hasOverdue) return "text-red-600";
  return "text-slate-700";
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function prazoStatus(prazo) {
  const today = todayStr();
  if (prazo.respondido === "Sim") return "done";
  if (prazo.data < today) return "overdue";
  const diff = (new Date(prazo.data) - new Date(today)) / 86400000;
  if (diff <= 7) return "urgent";
  return "normal";
}

const STATUS_DOT = {
  done:    "bg-emerald-500",
  overdue: "bg-red-500",
  urgent:  "bg-orange-500",
  normal:  "bg-[#0b679e]",
};

const STATUS_BADGE = {
  done:    { label: "Respondido", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  overdue: { label: "Em falta", cls: "bg-red-100 text-red-700 border-red-200" },
  urgent:  { label: "Urgente", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  normal:  { label: "Pendente", cls: "bg-blue-100 text-[#0b679e] border-blue-200" },
};

// ── AddPrazoModal ─────────────────────────────────────────────────────────────
function AddPrazoModal({ onClose, onSaved, defaultDate }) {
  const [processoSearch, setProcessoSearch] = useState("");
  const [processoResults, setProcessoResults] = useState([]);
  const [selectedProcesso, setSelectedProcesso] = useState(null);
  const [form, setForm] = useState({ data: defaultDate || "", descricao: "" });
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  const searchProcesso = useCallback(async (q) => {
    if (!q || q.length < 2) { setProcessoResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/processos?search=${encodeURIComponent(q)}&limit=8`, { headers: getAuthHeader() });
      setProcessoResults(data.processos || []);
    } catch (error) {
      setProcessoResults([]);
    }
    finally { setSearching(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchProcesso(processoSearch), 300);
    return () => clearTimeout(t);
  }, [processoSearch, searchProcesso]);

  const handleSave = async () => {
    if (!selectedProcesso) { toast.error("Selecione um processo"); return; }
    if (!form.data) { toast.error("Indique a data do prazo"); return; }
    if (!form.descricao.trim()) { toast.error("Indique uma descrição"); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/processos/${selectedProcesso.id}/prazos`,
        { processo_id: selectedProcesso.id, data: form.data, descricao: form.descricao, respondido: "Não" },
        { headers: getAuthHeader() }
      );
      toast.success("Prazo adicionado ao calendário");
      onSaved();
      onClose();
    } catch {
      toast.error("Erro ao guardar o prazo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-slate-800">Novo Prazo Judicial</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Processo search */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Processo *</label>
            {selectedProcesso ? (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-semibold text-[#0b679e] truncate">{selectedProcesso.nome_cliente}</p>
                  <p className="text-xs text-slate-500">{selectedProcesso.numero_processo}</p>
                </div>
                <button onClick={() => setSelectedProcesso(null)} className="text-slate-400 hover:text-red-500 ml-2 flex-shrink-0">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={processoSearch}
                  onChange={e => setProcessoSearch(e.target.value)}
                  placeholder="Pesquisar por cliente ou número..."
                  data-testid="prazo-processo-search"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0b679e]"
                />
                {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-[#0b679e] border-t-transparent rounded-full animate-spin" />}
                {processoResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                    {processoResults.map(p => (
                      <button
                        key={p.id}
                        onClick={() => { setSelectedProcesso(p); setProcessoSearch(""); setProcessoResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                      >
                        <p className="text-sm font-medium text-slate-800 truncate">{p.nome_cliente}</p>
                        <p className="text-xs text-slate-500">{p.numero_processo}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Data do Prazo *</label>
            <input
              type="date"
              value={form.data}
              onChange={e => setForm(f => ({ ...f, data: e.target.value }))}
              data-testid="prazo-data-input"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0b679e]"
            />
          </div>
          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Descrição *</label>
            <input
              type="text"
              value={form.descricao}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
              placeholder="Ex: Prazo de contestação, Recurso, etc."
              data-testid="prazo-descricao-input"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0b679e]"
            />
          </div>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="prazo-save-btn"
            className="flex-1 px-4 py-2 text-sm bg-[#0b679e] hover:bg-[#084e78] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DayCell ──────────────────────────────────────────────────────────────────
function DayCell({ day, year, month, prazos, isSelected, onClick }) {
  const isToday = `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}` === todayStr();
  const hasPrazos = prazos.length > 0;
  const statuses = prazos.map(prazoStatus);
  const hasOverdue = statuses.includes("overdue");
  const hasUrgent = statuses.includes("urgent");

  return (
    <button
      onClick={onClick}
      data-testid={`cal-day-${day}`}
      className={`relative min-h-[72px] p-2 rounded-xl border transition-all duration-150 text-left flex flex-col
        ${isSelected ? "border-[#0b679e] bg-[#0b679e]/5 shadow-sm" : "border-transparent hover:border-slate-200 hover:bg-slate-50"}
        ${isToday ? "ring-2 ring-[#0b679e] ring-offset-1" : ""}
      `}
    >
      <span className={`text-sm font-semibold leading-none mb-1.5 ${dayTextColor(isToday, hasOverdue)}`}>
        {day}
      </span>
      {hasPrazos && (
        <div className="flex flex-wrap gap-1 mt-auto">
          {prazos.slice(0, 3).map((p, i) => (
            <span key={p.id || `${p.processo_id}-${p.data}-${i}`} className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[prazoStatus(p)]}`} />
          ))}
          {prazos.length > 3 && (
            <span className="text-[9px] text-slate-400 leading-none mt-0.5">+{prazos.length - 3}</span>
          )}
        </div>
      )}
      {hasOverdue && !isSelected && (
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full" />
      )}
      {hasUrgent && !hasOverdue && !isSelected && (
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-orange-400 rounded-full" />
      )}
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PrazosCalendario() {
  const today = new Date();
  const [year, setYear]         = useState(today.getFullYear());
  const [month, setMonth]       = useState(today.getMonth() + 1);
  const [prazos, setPrazos]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [showModal, setShowModal]     = useState(false);
  const [updatingId, setUpdatingId]   = useState(null);

  const loadPrazos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/prazos-calendario?year=${year}&month=${month}`,
        { headers: getAuthHeader() }
      );
      setPrazos(data);
    } catch (error) {
      toast.error("Erro ao carregar prazos");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => { loadPrazos(); }, [loadPrazos]);

  // Group prazos by day number
  const prazosPerDay = {};
  prazos.forEach(p => {
    const d = parseInt((p.data || "").split("-")[2], 10);
    if (!prazosPerDay[d]) prazosPerDay[d] = [];
    prazosPerDay[d].push(p);
  });

  const selectedDayPrazos = prazosPerDay[selectedDay] || [];
  const totalDays = daysInMonth(year, month);
  const offset = dayOffset(year, month);
  const cells = [...Array(offset).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
    setSelectedDay(1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
    setSelectedDay(1);
  };

  const toggleRespondido = async (prazo) => {
    setUpdatingId(prazo.id);
    try {
      const newVal = prazo.respondido === "Sim" ? "Não" : "Sim";
      await axios.put(
        `${API}/processos/${prazo.processo_id}/prazos/${prazo.id}`,
        { respondido: newVal },
        { headers: getAuthHeader() }
      );
      setPrazos(prev => prev.map(p => p.id === prazo.id ? { ...p, respondido: newVal } : p));
      toast.success(newVal === "Sim" ? "Prazo marcado como respondido" : "Prazo reaberto");
    } catch {
      toast.error("Erro ao atualizar prazo");
    } finally {
      setUpdatingId(null);
    }
  };

  // Stats
  const totalPrazos = prazos.length;
  const overdueCount = prazos.filter(p => prazoStatus(p) === "overdue").length;
  const urgentCount  = prazos.filter(p => prazoStatus(p) === "urgent").length;
  const doneCount    = prazos.filter(p => prazoStatus(p) === "done").length;

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Calendar size={24} className="text-[#0b679e]" />
              Calendário de Prazos
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Prazos judiciais e processuais</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="novo-prazo-btn"
            className="flex items-center gap-2 bg-[#0b679e] hover:bg-[#084e78] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <Plus size={16} /> Novo Prazo
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total este mês", value: totalPrazos, cls: "text-slate-700", dot: "bg-slate-400" },
            { label: "Em falta", value: overdueCount, cls: "text-red-600", dot: "bg-red-500" },
            { label: "Urgentes (≤7 dias)", value: urgentCount, cls: "text-orange-600", dot: "bg-orange-500" },
            { label: "Respondidos", value: doneCount, cls: "text-emerald-600", dot: "bg-emerald-500" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full flex-shrink-0 ${s.dot}`} />
              <div>
                <p className={`text-xl font-bold ${s.cls}`}>{s.value}</p>
                <p className="text-xs text-slate-500 leading-tight">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Calendar + Side panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Calendar */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={prevMonth}
                data-testid="cal-prev-month"
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
              >
                <ChevronLeft size={18} />
              </button>
              <h2 className="text-base font-bold text-slate-800">
                {MESES_PT[month - 1]} {year}
              </h2>
              <button
                onClick={nextMonth}
                data-testid="cal-next-month"
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Day of week headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DIAS_PT.map(d => (
                <div key={d} className="text-center text-[11px] font-semibold text-slate-400 uppercase tracking-wide py-1">
                  {d}
                </div>
              ))}
            </div>

            {/* Day cells */}
            {loading ? (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                A carregar...
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {cells.map((day, i) =>
                  day === null ? (
                    <div key={`empty-${i}`} />
                  ) : (
                    <DayCell
                      key={day}
                      day={day}
                      year={year}
                      month={month}
                      prazos={prazosPerDay[day] || []}
                      isSelected={selectedDay === day}
                      onClick={() => setSelectedDay(day)}
                    />
                  )
                )}
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-100">
              {[
                { dot: "bg-red-500", label: "Em falta" },
                { dot: "bg-orange-500", label: "Urgente (≤7 dias)" },
                { dot: "bg-[#0b679e]", label: "Pendente" },
                { dot: "bg-emerald-500", label: "Respondido" },
              ].map(l => (
                <div key={l.label} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className={`w-2 h-2 rounded-full ${l.dot}`} />
                  {l.label}
                </div>
              ))}
            </div>
          </div>

          {/* Day panel */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="font-semibold text-slate-800 text-sm">
                {selectedDay ? `${selectedDay} de ${MESES_PT[month-1]} de ${year}` : "Selecione um dia"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {selectedDayPrazos.length === 0 ? "Sem prazos" : `${selectedDayPrazos.length} prazo${selectedDayPrazos.length !== 1 ? "s" : ""}`}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {selectedDayPrazos.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 size={32} className="text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Nenhum prazo neste dia</p>
                  <button
                    onClick={() => setShowModal(true)}
                    className="mt-3 text-xs text-[#0b679e] hover:underline"
                  >
                    + Adicionar prazo
                  </button>
                </div>
              ) : (
                selectedDayPrazos.map(prazo => {
                  const st = prazoStatus(prazo);
                  const badge = STATUS_BADGE[st];
                  return (
                    <div
                      key={prazo.id}
                      data-testid={`prazo-card-${prazo.id}`}
                      className="border border-slate-200 rounded-xl p-3 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800 leading-tight">{prazo.descricao || "Prazo"}</p>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>
                      {prazo.nome_cliente && (
                        <p className="text-xs text-slate-600 font-medium truncate">{prazo.nome_cliente}</p>
                      )}
                      {prazo.numero_processo && (
                        <p className="text-[11px] text-slate-400">{prazo.numero_processo}</p>
                      )}
                      <button
                        onClick={() => toggleRespondido(prazo)}
                        disabled={updatingId === prazo.id}
                        data-testid={`prazo-toggle-${prazo.id}`}
                        className={`w-full flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-lg border transition-colors
                          ${prazo.respondido === "Sim"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                          } disabled:opacity-50`}
                      >
                        {prazo.respondido === "Sim" ? (
                          <><CheckCircle2 size={12} /> Respondido — Reabrir</>
                        ) : (
                          <><Clock size={12} /> Marcar como Respondido</>
                        )}
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-4 border-t border-slate-100">
              <button
                onClick={() => setShowModal(true)}
                data-testid="add-prazo-day-btn"
                className="w-full flex items-center justify-center gap-2 border border-dashed border-slate-300 text-slate-500 hover:border-[#0b679e] hover:text-[#0b679e] rounded-lg py-2 text-xs font-medium transition-colors"
              >
                <Plus size={13} /> Adicionar prazo
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <AddPrazoModal
          onClose={() => setShowModal(false)}
          onSaved={loadPrazos}
          defaultDate={selectedDay
            ? `${year}-${String(month).padStart(2,"0")}-${String(selectedDay).padStart(2,"0")}`
            : ""}
        />
      )}
    </div>
  );
}
